/**
 * mobileMapCarousel.js
 *
 * Manages the bottom-of-page summary card carousel for mobile/tablet viewports
 * in the searchResultsMapVariant feature.
 *
 * Mirrors the pattern used by searchResultsMap/components/summaryCard.js:
 * the `[data-map-carousel]` container is shown/hidden and its inner
 * `[data-summary-card]` element is populated with card DOM elements.
 *
 * ## Dependencies
 * The constructor queries the DOM for `[data-map-carousel]` at call time.
 * Ensure the carousel container is present in the DOM before constructing.
 */

import fastdom from 'fastdom';

const CONTAINER_SELECTOR = '[data-map-carousel]';
const CARD_CONTAINER_SELECTOR = '[data-summary-card]';

export default class MobileMapCarousel {
  #container;

  #cardContainer;

  #observer = null;

  #observedElements = [];

  #previousCardPosition = null;

  #cardDataList = [];

  #onCardIntersected;

  constructor(onCardIntersected) {
    this.#onCardIntersected = onCardIntersected;
    this.#container = document.querySelector(CONTAINER_SELECTOR);
    this.#cardContainer = this.#container?.querySelector(CARD_CONTAINER_SELECTOR) ?? null;
  }

  get isAvailable() {
    return !!(this.#container && this.#cardContainer);
  }

  #createIntersectionObserver() {
    this.#observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          const card = entry.target;
          const currentCardPosition = Array.from(card.parentNode.children).indexOf(card) + 1;
          let direction = null;

          if (this.#previousCardPosition !== null) {
            direction = this.#previousCardPosition < currentCardPosition ? 'right' : 'left';
          }

          this.#previousCardPosition = currentCardPosition;

          if (!direction && currentCardPosition === 1) {
            return;
          }

          const listingId = card.getAttribute('data-id');
          const type = card.getAttribute('data-type');
          const activeItem = this.#cardDataList?.find((item) => String(item.id) === listingId) ?? null;

          this.#onCardIntersected?.({
                direction,
                currentCardPosition,
                nextCardPosition: currentCardPosition + 1,
                listingId,
                type,
                activeItem,
              });
        });
      },
      {
        root: this.#cardContainer,
        threshold: 1.0,
        rootMargin: '0px',
      },
    );
  }

  #observeCards(elements) {
    if (!this.#observer) {
      this.#createIntersectionObserver();
    }

    this.#observedElements.forEach((el) => this.#observer.unobserve(el));
    this.#observedElements = [];
    this.#previousCardPosition = null;

    elements
      .filter((el) => el.hasAttribute('data-id'))
      .forEach((el) => {
        this.#observer.observe(el);
        this.#observedElements.push(el);
      });
  }

  /**
   * Renders card elements into the carousel container and shows it.
   * Passing an owner string claims exclusive ownership of the carousel;
   * subsequent clear() calls from a different owner will be ignored.
   *
   * @param {HTMLElement[]} cardElements - Pre-built card DOM elements.
   * @param {Object[]} [cardDataList=[]] - Raw card data objects used for carouselScroll activeItem lookup.
   * @param {string|null} [owner=null] - Plugin identifier claiming ownership.
   */
  render(cardElements, cardDataList = [], owner = null) {
    if (!this.isAvailable || !cardElements?.length) {
      return;
    }

    if (owner !== null) {
      this.#container.dataset.carouselOwner = owner;
    }

    this.#cardDataList = cardDataList;

    const fragment = document.createDocumentFragment();
    cardElements.forEach((el) => {
      el.classList.add('nhs-c-carousel__element');
      fragment.appendChild(el);
    });

    fastdom.mutate(() => {
      this.#cardContainer.innerHTML = '';
      this.#cardContainer.appendChild(fragment);
      this.#container.removeAttribute('hidden');
      this.#observeCards(cardElements);
    });
  }

  /**
   * Clears the carousel content and hides the container.
   * When owner is provided, the clear is skipped if the carousel is currently
   * owned by a different plugin, preventing one plugin from wiping another's cards.
   *
   * @param {string|null} [owner=null] - Plugin identifier requesting the clear.
   */
  clear(owner = null) {
    if (!this.isAvailable) {
      return;
    }

    if (owner !== null && this.#container.dataset.carouselOwner !== owner) {
      return;
    }

    delete this.#container.dataset.carouselOwner;

    this.#observedElements.forEach((el) => this.#observer?.unobserve(el));
    this.#observedElements = [];
    this.#previousCardPosition = null;
    this.#cardDataList = [];

    fastdom.mutate(() => {
      this.#cardContainer.innerHTML = '';
      this.#container.setAttribute('hidden', '');
    });
  }

  /**
   * Returns whether the carousel is currently owned by the given plugin.
   *
   * @param {string} owner - Plugin identifier to check.
   * @returns {boolean}
   */
  isOwnedBy(owner) {
    return this.isAvailable && this.#container.dataset.carouselOwner === owner;
  }

  /**
   * Removes event listeners and clears the carousel.
   * Call this when the plugin is stopped.
   */
  destroy() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#observedElements = [];
    this.#previousCardPosition = null;
    this.#cardDataList = [];

    this.clear();
  }

  /**
   * Returns whether the carousel is currently visible in the DOM.
   * @returns {boolean}
   */
  isVisible() {
    if (!this.isAvailable) {
      return false;
    }
    return !this.#container.hasAttribute('hidden');
  }
}
